import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Language, LeftSidePopupsForAdding, ReturnedPortfolio } from '../Create-Portfolio/types';
import { Button, Form, Modal } from 'react-bootstrap';
import { languages as languageOptions } from '../../config/languages';
import { capitalizeFirstLetter } from '../UserProfile/helpers';
import { ReactComponent as SearchIcon } from '../../images/search-prompts.svg';
import { ReactComponent as EditIcon } from '../../images/pencil-square.svg';
import { ReactComponent as DeleteIcon } from '../../images/trash3-fill.svg';
import { Award, CheckCircle, Globe, GripVertical } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import {
  getLanguageLevelMeta,
  getLanguageMeta,
  LANGUAGE_LEVELS,
} from '../../utils/languageDisplay';
import { languagesEqual } from '../../utils/portfolioSectionMessages';
import './LanguageManageModal.css';

interface Props {
  setPopupOpen: React.Dispatch<React.SetStateAction<LeftSidePopupsForAdding>>;
  setPortfolioData?: React.Dispatch<React.SetStateAction<ReturnedPortfolio>>;
  setSavePortfolioButton?: React.Dispatch<React.SetStateAction<boolean>>;
  languages: Language[];
}

const LanguagePopup: React.FC<Props> = ({
  setPopupOpen,
  languages,
  setPortfolioData,
  setSavePortfolioButton,
}) => {
  const [draftLanguages, setDraftLanguages] = useState<Language[]>(languages);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [pendingLevel, setPendingLevel] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const searchWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDraftLanguages(languages);
    setSearchQuery('');
    setSelectedLanguage('');
    setPendingLevel('');
    setShowDropdown(false);
  }, [languages]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const excludedLanguages = useMemo(
    () => new Set(draftLanguages.map((lang) => lang.language.toLowerCase())),
    [draftLanguages]
  );

  const availableLanguages = useMemo(
    () => languageOptions.filter((item) => !excludedLanguages.has(item.toLowerCase())),
    [excludedLanguages]
  );

  const filteredLanguages = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return availableLanguages.slice(0, 12);
    }
    return availableLanguages.filter((item) => item.toLowerCase().includes(query));
  }, [availableLanguages, searchQuery]);

  const closeModal = () => {
    setPopupOpen((prev) => ({ ...prev, languages: false }));
  };

  const pickLanguage = (language: string) => {
    setSelectedLanguage(language);
    setSearchQuery(language);
    setShowDropdown(false);
  };

  const handleAddLanguage = () => {
    const languageName = capitalizeFirstLetter((selectedLanguage || searchQuery).trim());

    if (!languageName) {
      toast.error('Search for a language to add.');
      return;
    }

    if (!pendingLevel) {
      toast.error('Select a fluency level before adding.');
      return;
    }

    const alreadyAdded = draftLanguages.some(
      (lang) => lang.language.toLowerCase() === languageName.toLowerCase()
    );
    if (alreadyAdded) {
      toast.error('That language is already in your list.');
      return;
    }

    const matchedOption = languageOptions.find(
      (option) => option.toLowerCase() === languageName.toLowerCase()
    );
    const resolvedName = matchedOption ?? languageName;

    setDraftLanguages((prev) => [...prev, { language: resolvedName, level: pendingLevel }]);
    setSearchQuery('');
    setSelectedLanguage('');
    setPendingLevel('');
    setShowDropdown(false);
  };

  const updateLanguageLevel = (index: number, level: string) => {
    setDraftLanguages((prev) =>
      prev.map((item, itemIndex) => (itemIndex === index ? { ...item, level } : item))
    );
  };

  const removeLanguage = (index: number) => {
    setDraftLanguages((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (event: React.DragEvent<HTMLLIElement>, index: number) => {
    event.preventDefault();
    if (draggedIndex === null || draggedIndex === index) {
      return;
    }

    setDraftLanguages((prev) => {
      const next = [...prev];
      const [moved] = next.splice(draggedIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleDone = () => {
    const missingLevel = draftLanguages.some((lang) => !lang.level.trim());
    if (missingLevel) {
      toast.error('Select a fluency level for each language.');
      return;
    }

    const hasChanges = !languagesEqual(languages, draftLanguages);

    if (setPortfolioData && setSavePortfolioButton && hasChanges) {
      setPortfolioData((prev) => ({ ...prev, languages: draftLanguages }));
      setSavePortfolioButton(true);
    }

    closeModal();
  };

  return (
    <>
      <Modal.Header closeButton className="border-0 pb-0">
        <div>
          <Modal.Title className="languages-modal-title">
            <Globe size={22} className="languages-modal-globe" />
            Manage Languages
          </Modal.Title>
          <p className="languages-modal-subtitle">
            Add languages you know and select your fluency level for each.
          </p>
        </div>
      </Modal.Header>

      <Modal.Body>
        <section className="languages-add-section">
          <h3>Add New Language</h3>
          <div className="languages-add-row">
            <div className="languages-search-wrap" ref={searchWrapRef}>
              <SearchIcon className="languages-search-icon" aria-hidden />
              <Form.Control
                className="languages-search-input"
                type="text"
                value={searchQuery}
                placeholder="Search for a language (e.g. Spanish, French, Mandarin)"
                onFocus={() => setShowDropdown(true)}
                onChange={(event) => {
                  setSearchQuery(event.target.value);
                  setSelectedLanguage('');
                  setShowDropdown(true);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    if (filteredLanguages[0]) {
                      pickLanguage(filteredLanguages[0]);
                    }
                  }
                }}
              />
              {showDropdown && filteredLanguages.length > 0 && (
                <div className="languages-search-dropdown">
                  {filteredLanguages.map((item) => (
                    <button
                      key={item}
                      type="button"
                      className="languages-search-option"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => pickLanguage(item)}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Form.Select
              className="languages-level-select"
              aria-label="Select fluency level"
              value={pendingLevel}
              onChange={(event) => setPendingLevel(event.target.value)}
            >
              <option value="">Select level</option>
              {LANGUAGE_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </Form.Select>

            <Button
              variant="success"
              className="languages-add-btn"
              type="button"
              onClick={handleAddLanguage}
            >
              Add
            </Button>
          </div>
          <p className="languages-add-hint">
            You can add multiple languages and set fluency level for each.
          </p>
        </section>

        <section className="languages-list-section">
          <div className="languages-list-header">
            <span className="languages-list-title">Your languages ({draftLanguages.length})</span>
            {draftLanguages.length > 1 && (
              <span className="languages-drag-hint">
                <GripVertical size={14} />
                Drag to reorder
              </span>
            )}
          </div>

          {draftLanguages.length === 0 ? (
            <div className="languages-empty-list">
              Your languages will appear here after you add them.
            </div>
          ) : (
            <ul className="languages-list">
              {draftLanguages.map((item, index) => {
                const meta = getLanguageMeta(item.language);
                const levelMeta = getLanguageLevelMeta(item.level);
                const levelClass = levelMeta?.modalClass ?? 'lang-level-unset';

                return (
                  <li
                    key={`${item.language}-${index}`}
                    className={`languages-list-item ${levelClass} ${
                      draggedIndex === index ? 'is-dragging' : ''
                    }`}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(event) => handleDragOver(event, index)}
                    onDragEnd={handleDragEnd}
                  >
                    <span className="languages-drag-handle" aria-label="Drag to reorder">
                      <GripVertical size={16} />
                    </span>

                    <span className="languages-avatar">{meta.code}</span>

                    <div className="languages-item-copy">
                      <div className="languages-item-name">
                        <span>{item.language}</span>
                        {index === 0 && <span className="languages-primary-badge">Primary</span>}
                      </div>
                      <div className="languages-item-region">{meta.region}</div>
                    </div>

                    <Form.Select
                      className="languages-row-level-select"
                      aria-label={`Fluency level for ${item.language}`}
                      value={item.level}
                      onChange={(event) => updateLanguageLevel(index, event.target.value)}
                    >
                      <option value="">Select level</option>
                      {LANGUAGE_LEVELS.map((level) => (
                        <option key={level} value={level}>
                          {level}
                        </option>
                      ))}
                    </Form.Select>

                    <div className="languages-progress-wrap">
                      <div className="languages-progress-track">
                        <div
                          className="languages-progress-fill"
                          style={{
                            width: `${levelMeta?.progress ?? 0}%`,
                          }}
                        />
                      </div>
                      <span className="languages-progress-label">
                        {levelMeta ? `${levelMeta.progress}%` : '—'}
                      </span>
                    </div>

                    <div className="languages-row-actions">
                      <button
                        type="button"
                        className="languages-row-action-btn"
                        title="Edit language"
                        onClick={() => {
                          setSearchQuery(item.language);
                          setSelectedLanguage(item.language);
                          setPendingLevel(item.level);
                          removeLanguage(index);
                          setShowDropdown(false);
                        }}
                      >
                        <EditIcon style={{ width: '16px', height: '16px' }} />
                      </button>
                      <button
                        type="button"
                        className="languages-row-action-btn is-delete"
                        title="Remove language"
                        onClick={() => removeLanguage(index)}
                      >
                        <DeleteIcon style={{ width: '16px', height: '16px' }} />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {draftLanguages.length >= 2 && (
          <div className="languages-motivation-banner">
            <div className="languages-motivation-copy">
              <Award size={18} className="languages-motivation-icon" />
              <p className="languages-motivation-text">
                Great! Multiple languages increase your visibility. Candidates with multiple
                languages get 3x more profile views from recruiters.
              </p>
            </div>
            <div className="languages-motivation-status">
              <CheckCircle size={14} className="me-1" />
              {draftLanguages.length} Languages Added
              <br />
              Keep it up!
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <Button variant="success" className="languages-done-btn" type="button" onClick={handleDone}>
          Done
        </Button>
      </Modal.Footer>
    </>
  );
};

export default LanguagePopup;
